import React, { useState, useEffect } from 'react';
import {
  Card, Button, Table, Tag, Space, Modal, Form, Input, Upload, message,
  Tabs, Statistic, Row, Col, Typography, Empty, Spin, Popconfirm, Select, Tooltip
} from 'antd';
import {
  PlusOutlined, UploadOutlined, QrcodeOutlined, HistoryOutlined,
  TeamOutlined, GlobalOutlined, EyeOutlined, SendOutlined, ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function MiniProgramDeveloperPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [developerInfo, setDeveloperInfo] = useState(null);
  const [isDeveloper, setIsDeveloper] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [stats, setStats] = useState({ total: 0, published: 0, auditing: 0, rejected: 0 });
  const [showCreate, setShowCreate] = useState(false);
  const [createForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('all');

  // 检查开发者身份
  useEffect(() => {
    checkDeveloper();
  }, []);

  const checkDeveloper = async () => {
    try {
      const res = await api.get('/admin/mini-developer/info');
      setIsDeveloper(res.isDeveloper);
      setDeveloperInfo(res.developer);
      if (res.isDeveloper) {
        loadPrograms();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadPrograms = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/mini-developer/programs');
      setPrograms(res.list || []);
      // 计算统计
      const all = res.list || [];
      setStats({
        total: all.length,
        published: all.filter(p => p.status === 'published').length,
        auditing: all.filter(p => p.audit_status === 'auditing').length,
        rejected: all.filter(p => p.audit_status === 'rejected').length,
      });
    } catch (e) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  // 注册开发者
  const handleRegister = async (values) => {
    try {
      await api.post('/admin/mini-developer/register', values);
      message.success('开发者注册成功');
      checkDeveloper();
    } catch (e) {
      message.error(e.error || '注册失败');
    }
  };

  // 创建小程序
  const handleCreate = async (values) => {
    try {
      await api.post('/admin/mini-developer/programs', values);
      message.success('小程序创建成功');
      setShowCreate(false);
      createForm.resetFields();
      loadPrograms();
    } catch (e) {
      message.error(e.error || '创建失败');
    }
  };

  // 上传代码包
  const handleUpload = async (programId, values) => {
    try {
      await api.post(`/admin/mini-developer/programs/${programId}/upload`, values);
      message.success('代码包上传成功');
      loadPrograms();
    } catch (e) {
      message.error(e.error || '上传失败');
    }
  };

  // 提交审核
  const handleSubmitAudit = async (programId, versionId) => {
    try {
      await api.post(`/admin/mini-developer/programs/${programId}/submit-audit`, { versionId });
      message.success('已提交审核');
      loadPrograms();
    } catch (e) {
      message.error(e.error || '提交失败');
    }
  };

  // 查看详情
  const handleViewDetail = (id) => {
    navigate(`/admin/mini-programs/${id}`);
  };

  // 过滤程序列表
  const filteredPrograms = programs.filter(p => {
    if (activeTab === 'all') return true;
    if (activeTab === 'published') return p.status === 'published';
    if (activeTab === 'auditing') return p.audit_status === 'auditing';
    if (activeTab === 'rejected') return p.audit_status === 'rejected';
    if (activeTab === 'developing') return p.status === 'developing' || !p.status;
    return true;
  });

  const columns = [
    {
      title: '小程序名称',
      dataIndex: 'name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>appId: {record.app_id || '--'}</Text>
        </Space>
      ),
    },
    {
      title: '版本',
      dataIndex: 'online_version',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text>{text || record.current_version || '1.0.0'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.version_count || 0} 个版本</Text>
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (status, record) => {
        const statusMap = {
          published: { color: 'green', text: '已上线' },
          developing: { color: 'blue', text: '开发中' },
          pending: { color: 'orange', text: '待审核' },
          offline: { color: 'red', text: '已下架' },
          rejected: { color: 'red', text: '已驳回' },
        };
        const s = statusMap[status] || { color: 'default', text: status || '未知' };
        return (
          <Space direction="vertical" size={0}>
            <Tag color={s.color}>{s.text}</Tag>
            {record.audit_status === 'auditing' && <Tag color="processing">审核中</Tag>}
          </Space>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      render: (text) => text ? new Date(text).toLocaleDateString() : '--',
    },
    {
      title: '操作',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record.id)}>
            详情
          </Button>
          {record.audit_status === 'rejected' && (
            <Tooltip title={record.rejected_reason}>
              <Button size="small" type="link">驳回原因</Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // 加载状态
  if (loading && !developerInfo) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  // 非开发者：显示注册页面
  if (!isDeveloper) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: 24 }}>
        <Card>
          <Title level={4}>成为小程序开发者</Title>
          <Text type="secondary">注册开发者账号后，您可以创建、上传、发布小程序。</Text>
          <Form layout="vertical" onFinish={handleRegister} style={{ marginTop: 24 }}>
            <Form.Item name="realName" label="真实姓名" rules={[{ required: true, message: '请输入真实姓名' }]}>
              <Input placeholder="请输入真实姓名" />
            </Form.Item>
            <Form.Item name="idNumber" label="身份证号">
              <Input placeholder="请输入身份证号（个人开发者）" />
            </Form.Item>
            <Form.Item name="company" label="公司名称">
              <Input placeholder="请输入公司名称（企业开发者）" />
            </Form.Item>
            <Form.Item name="phone" label="联系电话">
              <Input placeholder="请输入联系电话" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block>
              注册开发者账号
            </Button>
          </Form>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>我的小程序</Title>
          <Text type="secondary">开发者：{developerInfo.real_name}</Text>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowCreate(true)}>
            创建小程序
          </Button>
        </Col>
      </Row>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card><Statistic title="全部小程序" value={stats.total} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="已上线" value={stats.published} valueStyle={{ color: '#3f8600' }} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="审核中" value={stats.auditing} valueStyle={{ color: '#1890ff' }} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="已驳回" value={stats.rejected} valueStyle={{ color: '#cf1322' }} /></Card>
        </Col>
      </Row>

      {/* 筛选标签 */}
      <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginBottom: 16 }}>
        <Tabs.TabPane tab="全部" key="all" />
        <Tabs.TabPane tab="已上线" key="published" />
        <Tabs.TabPane tab="审核中" key="auditing" />
        <Tabs.TabPane tab="已驳回" key="rejected" />
        <Tabs.TabPane tab="开发中" key="developing" />
      </Tabs>

      {/* 小程序列表 */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredPrograms}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10, total: filteredPrograms.length }}
          locale={{ emptyText: <Empty description="暂无小程序，点击右上角创建" /> }}
        />
      </Card>

      {/* 创建小程序弹窗 */}
      <Modal
        title="创建小程序"
        open={showCreate}
        onCancel={() => { setShowCreate(false); createForm.resetFields(); }}
        onOk={() => createForm.submit()}
        width={500}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="name" label="小程序名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="请输入小程序名称" maxLength={50} />
          </Form.Item>
          <Form.Item name="description" label="小程序描述">
            <TextArea placeholder="请输入小程序描述" rows={3} maxLength={200} />
          </Form.Item>
          <Form.Item name="categoryId" label="分类">
            <Select placeholder="请选择分类" allowClear>
              <Select.Option value={1}>工具</Select.Option>
              <Select.Option value={2}>生活服务</Select.Option>
              <Select.Option value={3}>娱乐</Select.Option>
              <Select.Option value={4}>教育</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="tags" label="搜索标签">
            <Input placeholder="多个标签用逗号分隔" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
